import { useEffect, useMemo, useRef, useState } from 'react';
import { PlatformHeader } from '../../../../shared/platform/PlatformHeader';
import { platformRoutes } from '../../../../shared/platform/platform-routes';
import { Icon } from '../../../../shared/ui/Primitives';
import {
  createRegionalMonitoringAdapter,
  regionalErrorMessage,
} from '../../adapters/regional-monitoring-http';
import {
  displayRegionalValue,
  formatRegionalTime,
  statusLabels,
  type CaseActionCommand,
  type ManualCaseCommand,
  type RegionalCasePage,
  type RegionalCell,
  type RegionalDetail,
  type RegionalFilter,
  type RegionalOverview,
  type RegionalPort,
  type RegionalWriteResult,
} from '../../consumer/regional-monitoring';
import { RegionalCaseDialog, RegionalManualDialog } from './RegionalCaseDialog';
import { RegionalMap } from './RegionalMap';
import '../../../overview/ui/overview.css';
import './regional-monitoring.css';

const sourceLabels = {
  READY: '公开天气已接通',
  DEGRADED: '最新天气 · 部分数据缺测',
  STALE: '天气数据已过期',
  UNAVAILABLE: '天气数据暂不可用',
};
const filters: { id: RegionalFilter; label: string }[] = [
  { id: 'OPEN', label: '待跟进' },
  { id: 'NEW', label: '待核查' },
  { id: 'VERIFYING', label: '核查中' },
  { id: 'HANDLING', label: '处理中' },
  { id: 'REVIEW_PENDING', label: '待复核' },
  { id: 'CLOSED', label: '已完成' },
  { id: 'ALL', label: '全部' },
];
const initialCase = () =>
  typeof window === 'undefined'
    ? ''
    : (new URLSearchParams(window.location.search).get('caseId') ?? '').slice(0, 100);
function caseUrl(id: string) {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  if (id) url.searchParams.set('caseId', id);
  else url.searchParams.delete('caseId');
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);
}

export function RegionalMonitoringPage({ suppliedPort }: { suppliedPort?: RegionalPort }) {
  const port = useMemo(() => suppliedPort ?? createRegionalMonitoringAdapter(), [suppliedPort]);
  const [overview, setOverview] = useState<RegionalOverview | null>(null),
    [page, setPage] = useState<RegionalCasePage | null>(null);
  const [filter, setFilter] = useState<RegionalFilter>('OPEN'),
    [selected, setSelected] = useState(''),
    [validTime, setValidTime] = useState('');
  const [loading, setLoading] = useState(true),
    [readError, setReadError] = useState(''),
    [queueError, setQueueError] = useState('');
  const [revision, setRevision] = useState(0),
    [notice, setNotice] = useState(''),
    [refreshing, setRefreshing] = useState(false);
  const [caseId, setCaseId] = useState(initialCase),
    [detail, setDetail] = useState<RegionalDetail | null>(null),
    [detailLoading, setDetailLoading] = useState(false),
    [detailError, setDetailError] = useState(''),
    [detailRevision, setDetailRevision] = useState(0);
  const [manual, setManual] = useState(false);
  const refreshRequest = useRef<string | null>(null);
  useEffect(() => {
    let disposed = false,
      sequence = 0;
    let active: AbortController | null = null;
    const read = async () => {
      active?.abort();
      const controller = new AbortController();
      active = controller;
      const version = ++sequence;
      setLoading(true);
      const results = await Promise.allSettled([
        port.overview(controller.signal),
        port.cases(filter, controller.signal),
      ]);
      if (disposed || version !== sequence || controller.signal.aborted) return;
      const [snapshot, queue] = results;
      if (snapshot.status === 'fulfilled') {
        setOverview(snapshot.value);
        setReadError('');
      } else setReadError(regionalErrorMessage(snapshot.reason));
      if (queue.status === 'fulfilled') {
        setPage(queue.value);
        setQueueError('');
      } else setQueueError(regionalErrorMessage(queue.reason));
      setLoading(false);
    };
    void read();
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void read();
    }, 60_000);
    return () => {
      disposed = true;
      active?.abort();
      clearInterval(timer);
    };
  }, [port, filter, revision]);
  useEffect(() => {
    if (!caseId) {
      setDetail(null);
      setDetailError('');
      setDetailLoading(false);
      return;
    }
    const controller = new AbortController();
    let disposed = false;
    setDetail(null);
    setDetailLoading(true);
    setDetailError('');
    void port
      .detail(caseId, controller.signal)
      .then((value) => {
        if (!disposed) {
          setDetail(value);
          setSelected(value.item.gridId);
        }
      })
      .catch((error) => {
        if (!disposed) setDetailError(regionalErrorMessage(error));
      })
      .finally(() => {
        if (!disposed) setDetailLoading(false);
      });
    return () => {
      disposed = true;
      controller.abort();
    };
  }, [caseId, detailRevision, port]);
  useEffect(() => {
    const changed = () => setCaseId(initialCase());
    window.addEventListener('popstate', changed);
    return () => window.removeEventListener('popstate', changed);
  }, []);
  const selectCase = (id: string) => {
    setCaseId(id);
    caseUrl(id);
  };
  const activeCell =
    overview?.cells.find((cell) => cell.id === selected) ?? overview?.cells[0] ?? null;
  const refresh = async () => {
    if (refreshing) return;
    refreshRequest.current ??= crypto.randomUUID();
    setRefreshing(true);
    setNotice('');
    try {
      const result = await port.refresh(refreshRequest.current);
      refreshRequest.current = null;
      setNotice(
        result.result === 'CACHED'
          ? '仍在来源缓存期，已读取最近有效快照。'
          : result.result === 'BUSY'
            ? '采集正在进行，稍后刷新列表即可查看。'
            : '公开天气已更新，扫描结果已保存。',
      );
      setRevision((n) => n + 1);
    } catch (error) {
      setNotice(regionalErrorMessage(error));
    } finally {
      setRefreshing(false);
    }
  };
  const create = async (command: ManualCaseCommand) => {
    const result = await port.create(command);
    setManual(false);
    setFilter('OPEN');
    selectCase(result.item.id);
    setNotice('核查事项已保存，可以继续开始核查。');
    setRevision((n) => n + 1);
  };
  const act = async (command: CaseActionCommand): Promise<RegionalWriteResult> => {
    const result = await port.act(caseId, command);
    setDetail(result);
    setNotice(
      result.idempotentReplay ? '已读回先前提交的结果，没有重复写入。' : '操作记录已保存到数据库。',
    );
    setRevision((n) => n + 1);
    return result;
  };
  return (
    <RegionalWorkspace
      overview={overview}
      page={page}
      filter={filter}
      setFilter={(value) => {
        if (value === filter) return;
        setFilter(value);
        setPage(null);
      }}
      selected={activeCell?.id ?? ''}
      select={setSelected}
      validTime={validTime}
      setValidTime={setValidTime}
      loading={loading}
      readError={readError}
      queueError={queueError}
      notice={notice}
      refreshing={refreshing}
      refresh={() => void refresh()}
      reload={() => setRevision((n) => n + 1)}
      openCase={selectCase}
      startManual={() => setManual(true)}
      endSession={() => {
        void port
          .endSession()
          .then(() => setNotice('本地操作会话已结束；数据库中的记录保留。'))
          .catch((error) => setNotice(regionalErrorMessage(error)));
      }}
    >
      {caseId && (
        <RegionalCaseDialog
          detail={detail}
          loading={detailLoading}
          error={detailError}
          close={() => selectCase('')}
          reload={() => setDetailRevision((n) => n + 1)}
          act={act}
        />
      )}
      {manual && overview && activeCell && (
        <RegionalManualDialog
          overview={overview}
          cell={activeCell}
          close={() => setManual(false)}
          create={create}
        />
      )}
    </RegionalWorkspace>
  );
}

interface WorkspaceProps {
  overview: RegionalOverview | null;
  page: RegionalCasePage | null;
  filter: RegionalFilter;
  setFilter: (filter: RegionalFilter) => void;
  selected: string;
  select: (id: string) => void;
  validTime: string;
  setValidTime: (time: string) => void;
  loading: boolean;
  readError: string;
  queueError: string;
  notice: string;
  refreshing: boolean;
  refresh: () => void;
  reload: () => void;
  openCase: (id: string) => void;
  startManual: () => void;
  endSession: () => void;
  children?: React.ReactNode;
}
export function RegionalWorkspace(props: WorkspaceProps) {
  const { overview, page } = props;
  const [horizon, setHorizon] = useState(72);
  const cell = overview?.cells.find((c) => c.id === props.selected) ?? overview?.cells[0] ?? null;
  const start = overview?.snapshot ? Date.parse(overview.snapshot.asOf) : 0;
  const times = [...new Set(overview?.cells.flatMap((c) => c.hourly.map((h) => h.time)) ?? [])]
    .sort()
    .filter((time) => Date.parse(time) < start + horizon * 3600000);
  const time = times.includes(props.validTime) ? props.validTime : (times[0] ?? '');
  const hour = cell?.hourly.find((h) => h.time === time);
  const totalOpen = overview
    ? overview.caseCounts.NEW +
      overview.caseCounts.VERIFYING +
      overview.caseCounts.HANDLING +
      overview.caseCounts.REVIEW_PENDING
    : null;
  const manualEnabled =
    !!overview?.snapshot && !!cell && cell.status !== 'UNAVAILABLE' && !props.readError;
  return (
    <div className="regional-risk">
      <PlatformHeader activePage="monitoring" monitoringMode="public-regional" />
      <main id="nm-main" className="regional-main">
        <header className="regional-page-heading">
          <div>
            <div className="regional-eyebrow">常态防范 / 区域监测</div>
            <h1>风险与预警</h1>
            <p>扫描整片区域的公开天气条件，把需要关注的事项跟进到底。</p>
          </div>
          <div className="regional-page-actions">
            <a href={`${platformRoutes.monitoring}?view=historical`}>
              卫星历史研究 <Icon name="arrow-right" />
            </a>
            <button className="regional-button" onClick={props.refresh} disabled={props.refreshing}>
              <Icon name="refresh" />
              {props.refreshing ? '正在更新天气…' : '更新天气'}
            </button>
            <button
              className="regional-button primary"
              onClick={props.startManual}
              disabled={!manualEnabled}
            >
              <Icon name="plus" />
              主动发起核查
            </button>
          </div>
        </header>
        <div className="regional-source-strip">
          <div>
            <span
              className={`regional-source-dot source-${props.readError ? 'UNAVAILABLE' : (overview?.source.state ?? 'UNAVAILABLE')}`}
            />
            <strong>{overview?.region.name ?? '杭州周边示例区'}</strong>
            <span>
              {props.readError
                ? '读取连接中断'
                : overview
                  ? overview.source.state === 'DEGRADED' && overview.source.errorCode !== null
                    ? '更新失败 · 保留上次快照'
                    : sourceLabels[overview.source.state]
                  : props.loading
                    ? '正在连接区域服务'
                    : '区域服务暂不可用'}
            </span>
          </div>
          <span>
            当前快照采集{' '}
            {overview?.source.fetchedAt
              ? `${formatRegionalTime(overview.source.fetchedAt)} 北京时间`
              : '尚无成功记录'}
          </span>
        </div>
        {(props.readError ||
          overview?.source.state === 'STALE' ||
          overview?.source.state === 'DEGRADED') && (
          <div className="regional-alert" role="alert">
            <Icon name="info" />
            <span>
              {props.readError ||
                (overview?.source.state === 'STALE'
                  ? '天气快照已过期，不再据此产生新的自动事项。已有核查记录继续保留。'
                  : overview?.source.errorCode !== null
                    ? `最近一次来源更新失败（尝试时间：${formatRegionalTime(overview?.source.lastAttemptAt ?? null)} 北京时间），当前展示上次成功快照（采集时间：${formatRegionalTime(overview?.source.fetchedAt ?? null)} 北京时间）。`
                    : `最新天气快照已采集（采集时间：${formatRegionalTime(overview.source.fetchedAt)} 北京时间），部分格点或小时缺测。仅对可用数据扫描，缺测不表示没有风险。`)}
            </span>
            <button className="regional-button" onClick={props.reload}>
              重新读取
            </button>
          </div>
        )}
        {props.notice && (
          <div className="regional-notice" role="status">
            {props.notice}
          </div>
        )}
        <div className="regional-stats">
          <button
            onClick={() => props.setFilter('OPEN')}
            className={props.filter === 'OPEN' ? 'is-active' : ''}
          >
            <span>待跟进事项</span>
            <strong>{totalOpen ?? '—'}</strong>
            <small>{overview ? `${overview.overdueCases} 项已到复核时间` : '等待数据库读回'}</small>
          </button>
          <button
            onClick={() => props.setFilter('NEW')}
            className={props.filter === 'NEW' ? 'is-active' : ''}
          >
            <span>等待核查</span>
            <strong>{overview?.caseCounts.NEW ?? '—'}</strong>
            <small>自动发现与人工关注统一呈现</small>
          </button>
          <button
            onClick={() => props.setFilter('REVIEW_PENDING')}
            className={props.filter === 'REVIEW_PENDING' ? 'is-active' : ''}
          >
            <span>等待复核</span>
            <strong>{overview?.caseCounts.REVIEW_PENDING ?? '—'}</strong>
            <small>核验处理依据，再完成记录</small>
          </button>
          <div>
            <span>可用采样格点</span>
            <strong>
              {overview
                ? `${overview.coverage.availableGrids + overview.coverage.partialGrids} / ${overview.coverage.uniqueGrids}`
                : '—'}
            </strong>
            <small>
              {overview
                ? `${overview.coverage.partialGrids} 个部分可用 · ${overview.coverage.unavailableGrids} 个不可用`
                : '缺测不会显示为零风险'}
            </small>
          </div>
        </div>
        <div className="regional-workspace-layout">
          <section
            className="regional-panel regional-area-panel"
            aria-labelledby="regional-area-title"
          >
            <div className="regional-panel-heading">
              <div>
                <h2 id="regional-area-title">区域预报</h2>
                <p>选择一个采样点，查看条件与来源</p>
              </div>
              <span className="regional-badge">原始模式预报</span>
            </div>
            {overview && overview.cells.length ? (
              <RegionalMap
                cells={overview.cells}
                bounds={overview.region.bounds}
                selected={cell?.id ?? ''}
                select={props.select}
              />
            ) : (
              <div className="regional-map-empty">
                <Icon name="pin" />
                <strong>{props.loading ? '正在读取区域数据' : '暂无可用天气快照'}</strong>
                <p>成功采集后，将显示真实格点与预报数据。</p>
              </div>
            )}
            <div className="regional-sample-selection">
              <div className="regional-sample-title">
                <strong>采样点</strong>
                <span>
                  {cell
                    ? `${cell.latitude.toFixed(4)}°N，${cell.longitude.toFixed(4)}°E`
                    : '尚未取得格点坐标'}
                </span>
              </div>
              <div className="regional-sample-grid">
                {overview?.cells.map((item, index) => (
                  <button
                    key={item.id}
                    className={`${item.id === cell?.id ? 'is-selected ' : ''}${item.status === 'UNAVAILABLE' ? 'is-unknown' : item.hitCount > 0 ? 'has-hit' : ''}`}
                    aria-pressed={item.id === cell?.id}
                    aria-label={`${sampleName(item, index)}，${item.status === 'UNAVAILABLE' ? '数据不可用' : item.hitCount ? '有规则命中' : '查看预报'}`}
                    onClick={() => props.select(item.id)}
                  >
                    {sampleName(item, index)}
                    {item.status === 'UNAVAILABLE' && <span>缺测</span>}
                  </button>
                ))}
              </div>
            </div>
            <div className="regional-time-controls">
              <div className="regional-time-heading">
                <strong>查看预报时刻</strong>
                <div role="group" aria-label="预报查看范围">
                  {[6, 24, 72].map((value) => (
                    <button
                      key={value}
                      aria-pressed={horizon === value}
                      onClick={() => setHorizon(value)}
                    >
                      {value} 小时
                    </button>
                  ))}
                </div>
              </div>
              <label htmlFor="regional-time-range">
                {time ? `${formatRegionalTime(time)} 北京时间` : '暂无有效时刻'}
              </label>
              <input
                id="regional-time-range"
                type="range"
                min={0}
                max={Math.max(0, times.length - 1)}
                value={Math.max(0, times.indexOf(time))}
                onChange={(event) => props.setValidTime(times[Number(event.target.value)] ?? '')}
                disabled={!times.length}
              />
              <div className="regional-range-labels">
                <span>{formatRegionalTime(times[0] ?? null)}</span>
                <span>{formatRegionalTime(times.at(-1) ?? null)}</span>
              </div>
            </div>
            <div className="regional-weather-values">
              <WeatherValue
                label="气温"
                value={displayRegionalValue(hour?.temperatureC, '°C')}
                threshold="高温条件 ≥ 35 °C"
              />
              <WeatherValue
                label="相对湿度"
                value={displayRegionalValue(hour?.humidityPct, '%')}
                threshold="≤ 30% 且风速 ≥ 5 m/s"
              />
              <WeatherValue
                label="风速"
                value={displayRegionalValue(hour?.windMs, 'm/s')}
                threshold="大风条件 ≥ 8 m/s"
              />
              <WeatherValue
                label="截至该时刻的 6 小时降雨"
                value={displayRegionalValue(hour?.rain6hMm, 'mm')}
                threshold="完整未来累计 ≥ 30 mm"
              />
            </div>
            {hour?.rain6hMm === null && (
              <p className="regional-inline-note">降雨累计窗口尚不完整或有缺值，不以 0 代替。</p>
            )}
            {cell?.issues.length ? (
              <p className="regional-alert">
                此格点存在数据问题，不能据此判断全部条件。问题标记：{cell.issues.join('、')}
              </p>
            ) : null}
            <p className="regional-inline-note">
              采样点代表天气模型格点，不覆盖室内或设备风险。阈值用于内部关注，不对应官方预警等级。
            </p>
          </section>
          <section className="regional-panel regional-queue" aria-labelledby="regional-queue-title">
            <div className="regional-panel-heading">
              <div>
                <h2 id="regional-queue-title">统一关注队列</h2>
                <p>发现 → 核查 → 处理 → 复核</p>
              </div>
              <button
                className="regional-icon-button"
                aria-label="刷新关注队列"
                onClick={props.reload}
                disabled={props.loading}
              >
                <Icon name="refresh" />
              </button>
            </div>
            <div className="regional-filters" role="group" aria-label="事项状态筛选">
              {filters.map((filter) => (
                <button
                  key={filter.id}
                  onClick={() => props.setFilter(filter.id)}
                  aria-pressed={props.filter === filter.id}
                >
                  {filter.label}
                </button>
              ))}
            </div>
            {props.queueError && (
              <div className="regional-alert" role="alert">
                {props.queueError}
                <button className="regional-button" onClick={props.reload}>
                  重试列表
                </button>
              </div>
            )}
            {!page && !props.queueError && (
              <p className="regional-empty" role="status">
                正在读取已保存的关注事项…
              </p>
            )}
            {page && page.items.length === 0 && !props.queueError && (
              <div className="regional-empty">
                <Icon name="clipboard" />
                <h3>{props.filter === 'OPEN' ? '当前没有待跟进事项' : '此状态下暂无记录'}</h3>
                <p>
                  {overview?.source.state === 'READY' && !props.readError
                    ? '已采集的天气条件未形成此列表中的事项。无命中不代表区域安全。'
                    : '数据不可用或尚未更新时，不能据此判断没有风险。'}
                </p>
                <button
                  className="regional-button"
                  onClick={props.startManual}
                  disabled={!manualEnabled}
                >
                  从选中位置发起核查
                </button>
              </div>
            )}
            {page && page.items.length > 0 && (
              <ul className="regional-case-list">
                {page.items.map((item) => (
                  <li key={item.id}>
                    <button onClick={() => props.openCase(item.id)}>
                      <div className="regional-case-row">
                        <span className={`regional-status state-${item.status}`}>
                          {statusLabels[item.status]}
                        </span>
                        {item.isOverdue && <span className="regional-overdue">已到复核时间</span>}
                        <span className="regional-origin">
                          {item.origin === 'THRESHOLD' ? '自动发现' : '人工关注'}
                        </span>
                      </div>
                      <h3>{item.title}</h3>
                      <p>{item.description}</p>
                      <div className="regional-case-meta">
                        <span>
                          {item.latitude.toFixed(3)}°N / {item.longitude.toFixed(3)}°E
                        </span>
                        <time>{formatRegionalTime(item.updatedAt)}</time>
                      </div>
                      <div className="regional-case-row">
                        <small>
                          {item.assignee || '待记录负责人'}
                          {item.dueAt ? ` · 复核 ${formatRegionalTime(item.dueAt)}` : ''}
                        </small>
                        <span className="regional-open-label">
                          查看并跟进 <Icon name="arrow-right" />
                        </span>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {page && (
              <div className="regional-list-count">
                显示 {page.items.length} / {page.total} 项
                {page.total > page.items.length ? '，当前结果已截断，请缩小状态范围。' : ''}
              </div>
            )}
          </section>
        </div>
        <details className="regional-source-details">
          <summary>数据来源、扫描规则与其他工作区</summary>
          <div className="regional-source-detail-grid">
            <section>
              <h3>当前数据</h3>
              <p>{overview?.source.attribution ?? '天气来源 Open-Meteo / ECMWF IFS'}</p>
              <p>
                扫描 {overview?.coverage.expectedHours ?? 72}{' '}
                小时，按有效格点逐小时检查。未启用订正模型。
              </p>
              <p>
                下次计划采集：{formatRegionalTime(overview?.source.nextRefreshAt ?? null)}
                ；模型起报时间由源接口提供情况决定，目前未知。
              </p>
              <button className="regional-button" onClick={props.endSession}>
                结束本地操作会话
              </button>
            </section>
            <section>
              <h3>本轮规则</h3>
              <ul>
                {overview?.rules.map((rule) => (
                  <li key={rule.id}>
                    {rule.label}：{rule.thresholdText}
                  </li>
                ))}
              </ul>
              <p>保留原始数据、版本和操作记录。复核完成不会自动发布预警或创建救援任务。</p>
            </section>
            <nav aria-label="其他风险工作区">
              <h3>其他工作区</h3>
              <a href={`${platformRoutes.monitoring}?view=historical`}>卫星历史研究</a>
              <a href={`${platformRoutes.monitoring}?mode=monitor&view=monitoring&dataSource=demo`}>
                合成监测演示
              </a>
              <a href={`${platformRoutes.monitoring}?view=records`}>原监测记录</a>
            </nav>
          </div>
        </details>
      </main>
      {props.children}
    </div>
  );
}
function sampleName(cell: RegionalCell, index: number): string {
  return cell.requestedPoints[0]?.id.toUpperCase() ?? `点 ${index + 1}`;
}
function WeatherValue({
  label,
  value,
  threshold,
}: {
  label: string;
  value: string;
  threshold: string;
}) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{threshold}</small>
    </div>
  );
}
